import { Link } from "react-router-dom";
import { ArrowRight, Target, Phone, Video, TrendingUp, Brain, Users, Terminal } from "lucide-react";

const features = [
  { icon: Target, title: "Onboarding Path", body: "8 step roadmap. Pre-lic → state exam → E&O → first sale." },
  { icon: Phone, title: "AI Dialer", body: "Mic records. Whisper transcribes. GPT-5.2 scores. Zero friction." },
  { icon: Video, title: "War Room", body: "Live agency room. Dial together. Close together." },
  { icon: Users, title: "Lead Pipeline", body: "6-stage Kanban. Every lead. Every dollar. Every source." },
  { icon: TrendingUp, title: "KPI Engine", body: "Income goal vs reality. $/dial. CPL. ROI by source." },
  { icon: Brain, title: "Leaderboard", body: "Weekly dials · closes · AI score. Competition closes deals." },
];

export default function Landing() {
  return (
    <div className="min-h-screen text-fg" style={{ background: "var(--bg)" }}>
      <header className="border-b border-term" style={{ background: "var(--bg-raised)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Terminal size={20} className="text-amber" strokeWidth={2}/>
            <div className="font-display text-xl leading-none text-amber">COYOTE/LIFE</div>
            <span className="text-[10px] font-mono text-fg-subtle ml-2">v1.0</span>
          </div>
          <div className="flex gap-2">
            <Link to="/login" data-testid="header-login-link" className="btn-ghost">Sign In</Link>
            <Link to="/signup" data-testid="header-signup-link" className="btn-primary">Onboard</Link>
          </div>
        </div>
      </header>

      {/* Ticker */}
      <div className="h-8 border-b border-term overflow-hidden flex items-center" style={{ background: "var(--bg-raised)" }}>
        <div className="ticker text-[11px] font-mono text-fg-muted tracking-widest uppercase">
          <span className="text-gain">● LIVE</span> · 8 agents dialing · $42,500 commission this month · 312 dials · avg AI score 82 · 4 new closes · <span className="text-electric">WAR ROOM OPEN</span> · next power hour 2:00 PM CST · agency growing · onboarding 3 new agents · &nbsp;&nbsp;
        </div>
      </div>

      <section className="relative grid-bg border-b border-term">
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 slide-up">
            <div className="term-line mb-6"><span className="w-2 h-2 bg-amber pulse-live"></span>High-Efficiency Agent Platform · EST. 2026</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-fg">
              Top 1% agents.<br />
              <span className="text-amber">Licensed. Earning.</span><br />
              Getting paid.
            </h1>
            <p className="mt-8 text-base text-fg-muted max-w-xl leading-relaxed font-mono">
              The high-efficiency platform built to supercharge agent success — from licensed to top producer.
              Guided onboarding. Live war room. AI-coached dialer. KPIs that don&apos;t lie.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-cta-signup" className="btn-primary">
                Onboard an Agent <ArrowRight size={14} />
              </Link>
              <Link to="/login" data-testid="hero-cta-login" className="btn-secondary">Already in &gt;_</Link>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg">
              <div>
                <div className="label-xs">Onboarding</div>
                <div className="font-display text-3xl text-amber mt-1">8 STEPS</div>
              </div>
              <div>
                <div className="label-xs">License → Sale</div>
                <div className="font-display text-3xl text-amber mt-1">0 → 1</div>
              </div>
              <div>
                <div className="label-xs">War Room</div>
                <div className="font-display text-3xl text-amber mt-1">24/7</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="border border-term rounded-lg p-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--bg-raised) 0%, var(--bg-subtle) 100%)" }}>
              <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(ellipse at top right, rgba(77,158,255,0.2), transparent 60%)" }}></div>
              <div className="relative">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-term">
                <div className="label-xs text-electric">AGENT TERMINAL / LIVE</div>
                <span className="text-[10px] font-mono text-gain flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-gain rounded-full pulse-live"></span>CONNECTED</span>
              </div>
              <div className="pb-4 border-b border-term">
                <div className="label-xs">Commission MTD</div>
                <div className="font-display text-5xl text-amber mt-2">$8,420<span className="blink text-fg-muted">_</span></div>
                <div className="text-xs text-fg-muted mt-1 font-mono">TARGET $15,000 · 56.13%</div>
                <div className="h-1.5 bg-[var(--bg-muted)] mt-3 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: "56%", background: "linear-gradient(90deg, var(--amber), var(--electric))" }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div className="border border-term rounded p-3" style={{ background: "var(--bg-raised)" }}>
                  <div className="label-xs">Dials</div>
                  <div className="font-display text-2xl mt-1 text-electric">247</div>
                </div>
                <div className="border border-term rounded p-3" style={{ background: "var(--bg-raised)" }}>
                  <div className="label-xs">Connect</div>
                  <div className="font-display text-2xl mt-1 text-gain">34%</div>
                </div>
                <div className="border border-term rounded p-3" style={{ background: "var(--bg-raised)" }}>
                  <div className="label-xs">$/Dial</div>
                  <div className="font-display text-2xl mt-1 text-amber">$34</div>
                </div>
                <div className="border border-term rounded p-3" style={{ background: "var(--bg-raised)" }}>
                  <div className="label-xs">AI Score</div>
                  <div className="font-display text-2xl mt-1 text-gain">82</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-[10px] font-mono">
                <span className="w-1.5 h-1.5 bg-live rounded-full pulse-live"></span>
                <span className="text-live">WAR ROOM · 4 AGENTS DIALING</span>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <div className="label-xs mb-3 text-electric">— THE STACK —</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-none">Everything your agents need.<br /><span className="text-fg-muted">Nothing they don&apos;t.</span></h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {features.map((f, i) => (
            <div key={f.title} className="border border-term p-7 rounded-md transition-all hover:border-electric hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[rgba(77,158,255,0.1)]" style={{ background: "var(--bg-raised)" }}>
              <f.icon size={22} strokeWidth={1.5} className={i % 2 === 0 ? "text-amber" : "text-electric"} />
              <div className="font-display text-xl mt-6 text-fg">{f.title}</div>
              <div className="text-sm text-fg-muted mt-2 leading-relaxed font-mono">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-term" style={{ background: "var(--bg-raised)" }}>
        <div className="max-w-7xl mx-auto px-6 py-20 text-center grid-bg">
          <div className="label-xs text-electric mb-4">&gt; READY TO EXECUTE</div>
          <h2 className="font-display text-4xl sm:text-6xl leading-none">Recruit_ <span className="blink text-amber">|</span></h2>
          <p className="mt-6 text-fg-muted max-w-md mx-auto font-mono text-sm">Your first agent onboards in under 10 minutes.</p>
          <Link to="/signup" data-testid="footer-cta-signup" className="btn-primary mt-10 inline-flex">
            Get Started <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-term py-5 px-6 text-[10px] text-fg-subtle font-mono flex justify-between max-w-7xl mx-auto uppercase tracking-widest" style={{ background: "var(--bg)" }}>
        <span>© 2026 COYOTE LIFE</span>
        <span>BUILT FOR AGENCY BUILDERS</span>
      </footer>
    </div>
  );
}
